import { useState, ReactNode } from 'react';
import { Box, Grid, IconButton, Menu, MenuItem, Typography, useTheme, } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BrandingWatermarkOutlinedIcon from '@mui/icons-material/BrandingWatermarkOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import ProtectedElement from '../../../auth/ProtectedElement';
import Confirmation from '../../../components/confirmation/Confirmation';
import { CredentialInput, deleteCredential, OfficialCredential } from '../../../networkRequest/officialsService';
import { Toast } from '@components';
import EditCredentialDialog from './EditCredentialDialog';
import { useUser } from '../../../hooks/useUser';
import { credentialManagementRoles } from '../../../utils/authorization';
import { SureCardIcon } from '../../../assets/icons/icons';
import { Chip } from '@baseComponents';
import { getCredentialTypeDisplayName } from '../../../utils/credential';

type FieldProps = {
  label: string;
  value: string | ReactNode;
  warning?: boolean;
};

type CredentialItemProps = {
  credential: OfficialCredential;
  refreshData: () => Promise<void>;
  setSuccessMessage: (string) => void;
};

const Field = ({ label, value, warning = false }: FieldProps) => {
  const theme = useTheme();
  const valueIsString = typeof value === 'string';
  return (
    <Grid
      container
      sx={{
        py: 1,
        alignItems: 'center'
      }}>
      <Grid sx={{ width: 160, flexShrink: 0 }}>
        <Typography variant='bodyMedium' sx={{
          fontWeight: 500
        }}>{label}</Typography>
      </Grid>
      <Grid size='grow' sx={{ minWidth: 0 }}>
        {
          valueIsString
            ? (
              <Typography
                variant='bodyMedium'
                sx={{ color: warning ? theme.palette.error.main : theme.palette.text.primary }}
              >
                {value}
              </Typography>
            )
            : value
        }
        
      </Grid>
    </Grid>
  );};

const formatDate = (date: string) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const CredentialItem = ({ credential, refreshData, setSuccessMessage }: CredentialItemProps) => {
  const { user } = useUser();
  const theme = useTheme();
  const [errorMessage, setErrorMessage] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const onDialogSave = async (_c: CredentialInput) => {
    setSuccessMessage('Credential updated');
    setIsDialogOpen(false);
    refreshData();
  };

  const onError = (error: string) => {
    setErrorMessage(error);
  };

  const remove = async () => {
    if (user) {
      const response = await deleteCredential(user.accessToken, credential.official_credential_id);
      if (response.error) {
        setErrorMessage(response.error);
      }

      if (response.data) {
        setSuccessMessage(`Credential deleted`);
        await refreshData();
        setIsConfirmationOpen(false);
      }
    } else {
      setErrorMessage('Failed to delete location, user not found');
    }
  };

  const credentialIconMap = {
    mobile: <PhoneIphoneIcon sx={{color: theme.palette.action.active}} />,
    card: <SureCardIcon />,
    sticker: <BrandingWatermarkOutlinedIcon sx={{color: theme.palette.action.active}} />,
  };

  const credentialStatusMap = {
    active: <Chip label='Active' color={theme.palette.teal[300]} size='small' />,
    pending: <Chip label='Not activated' color={theme.palette.warning.main} size='small' />,
    revoked: <Chip  textColor='light' label='Revoked' color={theme.palette.error.main} size='small' />,
  };

  return (
    <Grid
      size={{ xs: 12, sm: 6 }}
      sx={{
        padding: 2,
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '10px'
      }}
    >
      <Grid container spacing={2} sx={{
        justifyContent: 'space-between'
      }}>
        <Grid
          container
          sx={{
            alignItems: 'center',
            gap: 1
          }}>
          {credentialIconMap[credential.credential_type]}
          <Typography variant='s'>{getCredentialTypeDisplayName(credential.credential_type)}</Typography>
        </Grid>
        <ProtectedElement userProfile={user!.profile} allowedRoles={credentialManagementRoles}>
          <>
            <IconButton
              onClick={handleClickMenu}
              aria-label='Credential menu'
              disableFocusRipple
              disableRipple
              sx={{ padding: 0 }}
            >
              <MoreVertIcon fontSize='small' />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              {
                credential.status === 'pending'
                  ? null
                  : (
                    <MenuItem
                      onClick={() => {
                        setIsDialogOpen(true);
                        handleCloseMenu();
                      }}
                    >
                      Edit
                    </MenuItem>
                  )
              }
              
              <MenuItem
                onClick={() => {
                  setIsConfirmationOpen(true);
                  handleCloseMenu();
                }}
              >
                Delete
              </MenuItem>
            </Menu>
          </>
        </ProtectedElement>   
      </Grid>
      <Box sx={{
        mt: 1
      }}>
        <Field label='Status' value={credentialStatusMap[credential.status]} />
        <Field label='Client Provided' value={credential.client_provided ? 'Yes' : 'No'} />
        <Field
          label='Official Capacity ID'
          value={credential.official_credential_id}
        />
        <Field label='Updated On' value={formatDate(credential.updated_at)} />
      </Box>
      {errorMessage && (
        <Toast type='error' onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}
      <EditCredentialDialog
        credential={credential}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onError={onError}
        onSubmit={onDialogSave}
      />
      <Confirmation
        open={isConfirmationOpen}
        message='Deleting this credential is a permanent action. Are you certain you want to continue?'
        onConfirm={remove}
        onCancel={() => setIsConfirmationOpen(false)}
        confirmText='Delete'
        cancelText='Cancel'
        accessibleTitle='Delete Credential Confirmation'
      />
    </Grid>
  );
};

export default CredentialItem;
